import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, ShieldAlert, Settings, Users, RadioTower, ChevronLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const HIDDEN_ON = ["/", "/staff/login", "/live/", "/checkin/", "/wall/", "/portal", "/join", "/intake"];

const fmtTime = (dt) =>
  dt ? new Date(dt).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : "";

export default function ChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [control, setControl] = useState(null);
  const [showControl, setShowControl] = useState(false);
  const bottomRef = useRef(null);
  const prevUnread = useRef({});
  const inputCls =
    "flex-1 h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

  const loadState = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/state");
      setState((prev) => {
        if (prev && data.unread) {
          for (const [ch, n] of Object.entries(data.unread)) {
            const before = prevUnread.current[ch] || 0;
            if (n > before) {
              const label =
                data.channels.find((c) => c.id === ch)?.label ||
                data.people.find((p) => ch.includes(p.id))?.name || "Control";
              toast.message(`New message — ${label}`, { icon: <MessageCircle className="h-4 w-4" /> });
            }
          }
        }
        prevUnread.current = data.unread || {};
        return data;
      });
    } catch (e) {
      /* not logged in yet or network hiccup — stay quiet */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadState();
    const t = setInterval(loadState, 8000);
    return () => clearInterval(t);
  }, [user, loadState]);

  const loadMessages = useCallback(async (channel) => {
    try {
      const { data } = await api.get("/chat/messages", { params: { channel } });
      setMessages(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (!open || !active) return;
    loadMessages(active);
    const t = setInterval(() => loadMessages(active), 8000);
    return () => clearInterval(t);
  }, [open, active, loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/chat/messages", { channel: active, text: text.trim() });
      setText("");
      loadMessages(active);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleChat = async () => {
    try {
      const { data } = await api.post("/chat/toggle", { enabled: !state.enabled });
      toast.success(data.enabled ? "Chat switched on for everyone" : "Chat switched off for staff (you can still see control)");
      loadState();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadControl = async () => {
    try {
      const { data } = await api.get("/chat/control");
      setControl(data);
      setShowControl(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!user || HIDDEN_ON.some((p) => location.pathname === p || (p !== "/" && location.pathname.startsWith(p)))) {
    return null;
  }
  if (state && !state.enabled) return null;

  const unreadTotal = Object.values(state?.unread || {}).reduce((a, b) => a + b, 0);
  const dmChannel = (personId) => `dm:${[user.id, personId].sort().join(":")}`;

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-bubble"
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white shadow-lg grid place-items-center print:hidden"
        title="Event chat"
      >
        <MessageCircle className="h-6 w-6" />
        {unreadTotal > 0 && (
          <span data-testid="chat-unread-badge"
            className="absolute -top-1 -right-1 h-6 min-w-6 px-1 rounded-full bg-[#D5281B] text-white text-[11px] font-bold grid place-items-center">
            {unreadTotal}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-50 w-[calc(100vw-40px)] max-w-sm h-[520px] max-h-[70vh] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden print:hidden"
        >
          <div className="bg-[#0F2547] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <p className="font-heading font-bold text-sm flex items-center gap-2">
              <RadioTower className="h-4 w-4 text-sky-300" />
              {showControl ? "Control — all conversations" : active ? "Chat" : "Event chat"}
              {state && !state.enabled && <span className="text-[10px] font-bold text-amber-300 uppercase">(off for staff)</span>}
            </p>
            <div className="flex items-center gap-1">
              {state?.is_admin && (
                <button onClick={toggleChat} data-testid="chat-toggle-btn" title={state.enabled ? "Switch chat off for staff" : "Switch chat on"}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors min-h-[36px]">
                  <Settings className={`h-4 w-4 ${state.enabled ? "text-emerald-300" : "text-amber-300"}`} />
                </button>
              )}
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn"
                className="p-2 rounded-lg hover:bg-white/10 transition-colors min-h-[36px]">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {showControl ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-control-view">
              <button onClick={() => setShowControl(false)} className="text-xs font-bold text-[#005EB8] hover:underline mb-1 min-h-[32px] inline-flex items-center gap-1">
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
              {(control || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No conversations yet.</p>
              ) : (
                control.map((c) => (
                  <button key={c.channel} onClick={() => { setActive(c.channel); setShowControl(false); }}
                    data-testid={`control-channel-${c.channel.replace(/[^a-z0-9]+/gi, "-")}`}
                    className="w-full text-left rounded-xl border border-slate-200 p-3 hover:border-[#005EB8] transition-colors">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-[#0F2547] truncate">
                        {c.channel.startsWith("event:") ? c.channel.slice(6) : "Direct message"}
                      </p>
                      <span className="text-[10px] text-slate-400">{c.count} msg</span>
                    </div>
                    {c.last_message && (
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {c.last_message.sender_name}: {c.last_message.text}
                      </p>
                    )}
                  </button>
                ))
              )}
            </div>
          ) : !active ? (
            <div className="flex-1 overflow-y-auto p-3" data-testid="chat-channel-list">
              {state?.channels?.length > 0 && (
                <>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 mb-1.5">Your events today</p>
                  {state.channels.map((c) => (
                    <button key={c.id} onClick={() => setActive(c.id)} data-testid={`chat-channel-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="w-full text-left rounded-xl border border-slate-200 p-3 mb-2 hover:border-[#005EB8] transition-colors flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#0F2547] flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#005EB8]" /> {c.label}
                      </span>
                      {state.unread?.[c.id] > 0 && (
                        <span className="h-5 min-w-5 px-1 rounded-full bg-[#D5281B] text-white text-[10px] font-bold grid place-items-center">{state.unread[c.id]}</span>
                      )}
                    </button>
                  ))}
                </>
              )}
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 mb-1.5 mt-3">Direct messages</p>
              {(state?.people || []).length === 0 ? (
                <p className="text-xs text-slate-400 px-1">No other staff accounts yet.</p>
              ) : (
                state.people.map((p) => {
                  const ch = dmChannel(p.id);
                  return (
                    <button key={p.id} onClick={() => setActive(ch)} data-testid={`chat-dm-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="w-full text-left rounded-xl border border-slate-200 p-3 mb-2 hover:border-[#005EB8] transition-colors flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#0F2547]">{p.name}</span>
                      {state.unread?.[ch] > 0 && (
                        <span className="h-5 min-w-5 px-1 rounded-full bg-[#D5281B] text-white text-[10px] font-bold grid place-items-center">{state.unread[ch]}</span>
                      )}
                    </button>
                  );
                })
              )}
              {state?.is_admin && (
                <button onClick={loadControl} data-testid="chat-control-btn"
                  className="w-full mt-2 rounded-xl border border-dashed border-slate-300 p-3 text-xs font-bold text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]">
                  Control view — see every conversation
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 shrink-0">
                <button onClick={() => { setActive(null); loadState(); }} data-testid="chat-back-btn"
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors min-h-[32px]">
                  <ChevronLeft className="h-4 w-4 text-slate-500" />
                </button>
                <p className="text-xs font-bold text-[#0F2547] truncate" data-testid="chat-active-label">
                  {active.startsWith("event:")
                    ? active.slice(6)
                    : state?.people.find((p) => active.includes(p.id))?.name || "Direct message"}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F4F7FA]" data-testid="chat-messages">
                {messages.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-6">No messages yet — say hello.</p>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  if (m.kind === "alert") {
                    return (
                      <div key={m.id} data-testid={`chat-alert-${m.id}`}
                        className="rounded-xl bg-red-600 text-white p-3 shadow-md">
                        <p className="text-[10px] font-extrabold uppercase tracking-widest flex items-center gap-1.5 mb-1">
                          <ShieldAlert className="h-3.5 w-3.5" /> Alert — {m.sender_name} · {fmtTime(m.created_at)}
                        </p>
                        <p className="text-sm font-bold">{m.text}</p>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-[#005EB8] text-white" : "bg-white border border-slate-200"}`}>
                        {!mine && <p className="text-[10px] font-bold text-[#005EB8] mb-0.5">{m.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                        <p className={`text-[9px] mt-0.5 ${mine ? "text-sky-200" : "text-slate-400"}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2 shrink-0">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message…"
                  data-testid="chat-input" className={inputCls} />
                <button type="submit" disabled={!text.trim()} data-testid="chat-send-btn"
                  className="h-11 w-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white grid place-items-center disabled:opacity-50 shrink-0">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
